/**
 * Provider helpers for multi-IdP UI (H.IMPL.8, AUTH0.IMPL.1).
 */

import { getProviderMeta, getProviderMetaForProject, resolveProviderId } from "./providerContext.js";

/**
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 */
export function resolveProjectProviderId(project) {
  return resolveProviderId(project);
}

/**
 * @param {{ providerId?: string } | null | undefined} project
 */
export function isEntraProject(project) {
  return resolveProjectProviderId(project) === "entra";
}

/**
 * Okta-only workspace surfaces gate on this (not credentialless state).
 *
 * @param {{ providerId?: string } | null | undefined} project
 */
export function isOktaProject(project) {
  if (project == null) return false;
  return resolveProjectProviderId(project) === "okta";
}

/**
 * @param {{ providerId?: string } | null | undefined} project
 */
export function isAuth0Project(project) {
  return resolveProjectProviderId(project) === "auth0";
}

/**
 * @param {{ providerId?: string } | null | undefined} project
 */
export function isOryProject(project) {
  return resolveProjectProviderId(project) === "ory";
}

/**
 * Auth0 project with stored M2M credentials (AUTH0.IMPL.3).
 *
 * @param {{ providerId?: string, credentialValidatedAt?: string | null } | null | undefined} project
 */
export function isAuth0Connected(project) {
  if (!isAuth0Project(project)) return false;
  return Boolean(project?.credentialValidatedAt);
}

/**
 * Ory project with stored Project API key (POST-ARC.12a).
 *
 * @param {{ providerId?: string, credentialValidatedAt?: string | null } | null | undefined} project
 */
export function isOryConnected(project) {
  if (!isOryProject(project)) return false;
  return Boolean(project?.credentialValidatedAt);
}

/**
 * @param {{ credentialMetadata?: { deployment_mode?: string } | null } | null | undefined} project
 */
export function getOryDeploymentMode(project) {
  const mode = String(project?.credentialMetadata?.deployment_mode || "").toLowerCase();
  return mode === "self_hosted" ? "self_hosted" : "network";
}

/**
 * @param {{ providerId?: string, credentialMetadata?: { deployment_mode?: string } | null } | null | undefined} project
 */
export function isOrySelfHostedProject(project) {
  return isOryProject(project) && getOryDeploymentMode(project) === "self_hosted";
}

/**
 * Connected self-hosted Ory — credentials stored; uses workspace flow (not discovery chat).
 *
 * @param {{ providerId?: string, credentialValidatedAt?: string | null, credentialMetadata?: { deployment_mode?: string } | null } | null | undefined} project
 */
export function isOrySelfHostedConnected(project) {
  return isOrySelfHostedProject(project) && isOryConnected(project);
}

/**
 * Self-hosted Ory collection path — config upload vs live Admin API.
 *
 * @param {{ credentialMetadata?: { collection_mode?: string } | null } | null | undefined} project
 * @returns {"config_upload" | "live_admin_api"}
 */
export function getOryCollectionMode(project) {
  const mode = String(project?.credentialMetadata?.collection_mode || "").toLowerCase();
  return mode === "config_upload" ? "config_upload" : "live_admin_api";
}

/**
 * CommandWindow header breadcrumb — Ory workspaces use deployment/collection context;
 * all other providers keep the navigation tree path unchanged.
 *
 * @param {{ providerId?: string, credentialMetadata?: { deployment_mode?: string, collection_mode?: string } | null } | null | undefined} project
 * @param {string} selectedTreePath
 * @returns {string}
 */
export function getWorkspaceBreadcrumbLabel(project, selectedTreePath) {
  if (!isOryProject(project)) {
    return selectedTreePath;
  }
  if (isOrySelfHostedProject(project)) {
    if (getOryCollectionMode(project) === "config_upload") {
      return "Self-hosted Ory > Configuration assessment";
    }
    return "Self-hosted Ory > Admin API assessment";
  }
  return "Ory Network > Security assessment";
}

/**
 * Entra project with stored Graph credentials (POST-ARC.8).
 *
 * @param {{ providerId?: string, credentialValidatedAt?: string | null } | null | undefined} project
 */
export function isEntraConnected(project) {
  if (!isEntraProject(project)) return false;
  return Boolean(project?.credentialValidatedAt);
}

const AUTH0_RECOMMENDED_SCOPES = [
  "read:guardian_factors",
  "read:authentication_methods",
  "read:role_members",
];

/**
 * Recommended Auth0 M2M scopes missing from stored credential metadata (IMPL.5).
 *
 * @param {{ providerId?: string, credentialMetadata?: { scopes_granted?: string[] } | null } | null | undefined} project
 * @returns {string[]}
 */
export function auth0MissingRecommendedScopes(project) {
  if (!isAuth0Connected(project)) return [];
  const granted = Array.isArray(project?.credentialMetadata?.scopes_granted)
    ? project.credentialMetadata.scopes_granted
    : [];
  const set = new Set(granted);
  return AUTH0_RECOMMENDED_SCOPES.filter((s) => !set.has(s));
}

/**
 * Projects that open the workspace without an Okta SSWS reconnect modal.
 * Entra: credentials server-side. Auth0 (stub): no session token until IMPL.3 connect UI.
 *
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 */
export function shouldSkipResumeModal(project) {
  const id = resolveProjectProviderId(project);
  if (id === "entra") return true;
  // AUTH0.IMPL.1-rev2: all Auth0 projects are stub-state until IMPL.3 ships real credentials.
  if (id === "auth0") return true;
  if (id === "ory") return true;
  return false;
}

/**
 * Credentialless workspace — no Okta SSWS session required to open the project (rev2/rev3 UI).
 * Entra + Auth0 stub: skip Connect Okta, hide roadmap/approvals where applicable.
 *
 * LLM short-circuits (rev4): only Auth0 stub skips architect LLM bootstrap; Entra still uses LLM discovery.
 * Use `isAuth0StubProject()` from credentiallessArchitect.js for Auth0-only LLM routing (unconnected Auth0 only; connected uses LLM architect per IMPL.4-rev).
 */
export function isCredentiallessWorkspace(project) {
  if (isAuth0Project(project)) {
    return !isAuth0Connected(project);
  }
  if (isOryProject(project)) {
    return !isOryConnected(project);
  }
  return shouldSkipResumeModal(project);
}

/**
 * Header badge label (uppercase styling applied in Topbar).
 *
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 */
export function getProviderBadgeLabel(project) {
  return getProviderMetaForProject(project).projectHeaderPrefix;
}

/**
 * @param {{ org?: { url?: string }, orgUrl?: string, tenantIdentifier?: string } | null | undefined} project
 */
export function getProjectTenantDisplayUrl(project) {
  return (
    project?.org?.url ||
    project?.orgUrl ||
    project?.tenantIdentifier ||
    ""
  );
}

/**
 * @param {{ providerId?: string, org?: { name?: string, url?: string } } | null | undefined} project
 */
export function projectHeaderLabel(project) {
  const name = project?.org?.name || "Project";
  const meta = getProviderMetaForProject(project);
  return `${meta.projectHeaderPrefix} · ${name}`;
}

/** Entra chat placeholder — preserved byte-for-byte from rev3 (distinct phrasing from Okta/Auth0). */
const ENTRA_COMMAND_CHAT_PLACEHOLDER =
  "Ask about your Entra ID tenant, Conditional Access, or findings…";

/**
 * CommandWindow chat input placeholder — catalog-driven for Okta/Auth0; Entra uses legacy string.
 *
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 */
export function getCommandWindowChatPlaceholder(project) {
  const id = resolveProjectProviderId(project);
  if (id === "entra") return ENTRA_COMMAND_CHAT_PLACEHOLDER;
  const meta = getProviderMetaForProject(project);
  return `Ask anything about your ${meta.displayName} setup…`;
}

/**
 * OrgConnecting subhead — catalog-driven for Okta / Entra / Auth0 (/connect is URL-reachable for all).
 *
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 */
export function getOrgConnectingAuditSubhead(project) {
  const meta = getProviderMetaForProject(project);
  return `Running a full ${meta.displayName} org audit — this usually takes a few seconds.`;
}

/**
 * OrgConnecting tenant display — Okta uses SSWS session org URL; Entra/Auth0 use project metadata.
 *
 * @param {{ providerId?: string, org?: { name?: string, url?: string }, tenantIdentifier?: string, orgUrl?: string } | null | undefined} project
 * @param {string | null | undefined} sessionOrg
 */
export function getOrgConnectingDisplayTarget(project, sessionOrg) {
  const id = resolveProjectProviderId(project);
  if (id === "okta") {
    return sessionOrg?.trim() || "your org";
  }
  if (id === "entra") {
    return project?.org?.name || project?.org?.url || "your Entra tenant";
  }
  const meta = getProviderMetaForProject(project);
  const tenant = getProjectTenantDisplayUrl(project);
  return tenant.trim() || `your ${meta.tenantLabel}`;
}

/**
 * success_card admin-console link label — catalog `adminConsoleName`.
 *
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 */
export function getOpenInAdminConsoleLabel(project) {
  const meta = getProviderMetaForProject(project);
  return `Open in ${meta.adminConsoleName}`;
}

/**
 * Whether OrgConnecting requires Okta SSWS session before running audit.
 *
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 */
export function orgConnectingRequiresOktaSession(project) {
  return resolveProjectProviderId(project) === "okta";
}

/**
 * Provider-routed org-audit request for OrgConnecting (AUTH0.IMPL.1-rev5).
 *
 * @param {{ providerId?: string, provider_id?: string } | null | undefined} project
 * @param {{ projectId: string, orgUrl?: string, apiToken?: string }} params
 * @returns {{ url: string, body: Record<string, unknown> }}
 */
export function getOrgConnectingAuditRequest(project, { projectId, orgUrl, apiToken }) {
  const id = resolveProjectProviderId(project);
  const pid = String(projectId || "").trim();
  if (!pid) {
    throw new Error("Missing project.");
  }
  if (id == null) {
    throw new Error("Project provider not loaded");
  }
  if (id === "entra") {
    return { url: "/api/idp/entra/org-audit", body: { projectId: pid } };
  }
  if (id === "auth0") {
    return { url: "/api/idp/auth0/org-audit", body: { projectId: pid } };
  }
  if (id === "ory") {
    return { url: "/api/idp/ory/org-audit", body: { projectId: pid } };
  }
  if (id === "okta") {
    const ou = String(orgUrl || "").trim();
    const tok = String(apiToken || "").trim();
    if (!ou || !tok) {
      throw new Error("Missing session — return to the dashboard and connect again.");
    }
    return {
      url: "/api/idp/okta/org-audit",
      body: { orgUrl: ou, apiToken: tok, projectId: pid },
    };
  }
  throw new Error(`Unsupported identity provider: ${id}`);
}
